// Copies that start in the click itself.
//
// Safari (and every iOS browser) only lets a page write to the clipboard while the user's click is being handled.
// A Blazor copy button ran click -> .NET -> JS writeText, and by the time .NET answered the gesture was gone, so
// WebKit refused the write. A copy element now carries data-dws-copy="<key>" and data-dws-copy-kind
// ("text" | "html" | "image"). This listener runs in the click (capture phase, before Blazor's own handler) and
// starts navigator.clipboard.write() right there with a ClipboardItem whose data is still a promise - the pattern
// Safari supports. .NET then calls dwsClipboardCopy(key, kind, payload) (module "clipboard"): it resolves that
// promise with the content and waits for the write. Without a pending write for the key (no click, no ClipboardItem
// support, a stale key) dwsClipboardCopy copies directly the old way, so programmatic copies keep working.
// Core: the listener has to be in place before the first click; the pending writes are shared with the module
// through dwsClipboardPending.

import { publish } from "../shared/publish";

// A pending write nobody resolves gives up after this long, so a click .NET ignored leaves nothing behind.
const DWS_CLIPBOARD_PENDING_TIMEOUT = 20000;

/** What the content of a copy resolves to: text, HTML with its text alternative, or a PNG image. */
interface DwsClipboardPayload {
    text?: string;
    html?: string;
    image?: Blob;
    alt?: string;
}

/** A write started in a click, waiting for .NET to supply its content (dwsClipboardCopy in module "clipboard"). */
interface DwsClipboardPendingWrite {
    timer: ReturnType<typeof setTimeout>;
    resolve: (payload: DwsClipboardPayload) => void;
    reject: (error: Error) => void;
    [key: string]: unknown;
}

// By data-dws-copy key. On window, as before: the clipboard module resolves these.
const dwsClipboardPending: Record<string, DwsClipboardPendingWrite> = {};
publish("dwsClipboardPending", dwsClipboardPending);

// Whether the clipboard takes a type. Browsers without ClipboardItem.supports are taken at their word.
function dwsClipboardSupports(type: string): boolean {
    if (typeof ClipboardItem === 'undefined') {
        return false;
    }
    if (typeof ClipboardItem.supports !== 'function') {
        return true;
    }
    try {
        return ClipboardItem.supports(type);
    } catch (err) {
        return false;
    }
}

function dwsClipboardCancel(key: string): void {
    const pending = dwsClipboardPending[key];
    if (!pending) {
        return;
    }
    delete dwsClipboardPending[key];
    clearTimeout(pending.timer);
    pending.reject(new Error('Copy cancelled'));
}

function dwsClipboardOnClick(event: MouseEvent): void {
    const target = event.target as Element;
    if (!target || typeof target.closest !== 'function') {
        return;
    }
    const element = target.closest<HTMLButtonElement>('[data-dws-copy]');
    if (!element || element.disabled || element.getAttribute('aria-disabled') === 'true') {
        return;
    }
    if (typeof ClipboardItem === 'undefined' || !navigator.clipboard || typeof navigator.clipboard.write !== 'function') {
        return;
    }

    const key = element.getAttribute('data-dws-copy');
    if (!key) {
        return;
    }
    const kind = element.getAttribute('data-dws-copy-kind') || 'text';

    dwsClipboardCancel(key);

    let resolveData;
    let rejectData;
    const data = new Promise<DwsClipboardPayload>(function (resolve, reject) {
        resolveData = resolve;
        rejectData = reject;
    });
    data.catch(function () { });

    // Only types the clipboard takes: one it does not refuses the whole item. A browser that holds no images starts
    // nothing here; .NET's call then tries the old way and reports the failure (never a different content).
    const items = {};
    let needsImage = false;
    if (kind === 'image') {
        if (dwsClipboardSupports('image/png')) {
            needsImage = true;
            items['image/png'] = data.then(function (d) { return d.image; });
        } else {
            return;
        }
    } else if (kind === 'html') {
        if (dwsClipboardSupports('text/html')) {
            items['text/html'] = data.then(function (d) { return new Blob([d.html], { type: 'text/html' }); });
        }
        items['text/plain'] = data.then(function (d) { return new Blob([d.text], { type: 'text/plain' }); });
    } else {
        items['text/plain'] = data.then(function (d) { return new Blob([d.text], { type: 'text/plain' }); });
    }

    let write;
    try {
        write = navigator.clipboard.write([new ClipboardItem(items)]);
    } catch (err) {
        // A browser that takes no promise in a ClipboardItem: .NET's call copies the old way.
        return;
    }
    write.catch(function () { });

    const pending = {
        kind: kind,
        needsImage: needsImage,
        write: write,
        resolve: resolveData,
        reject: rejectData,
        timer: setTimeout(function () { dwsClipboardCancel(key); }, DWS_CLIPBOARD_PENDING_TIMEOUT)
    };
    dwsClipboardPending[key] = pending;
}

document.addEventListener('click', dwsClipboardOnClick, true);

publish("dwsClipboardCancel", dwsClipboardCancel);
